import moment from "moment";

import {
  PAYMENT_REQUEST_STATUS,
  REVIEW_DISPLAY_IN_CORRECTION,
  REVIEW_DISPLAY_SUBMITTED,
  REVIEW_STATUS,
} from "../../../../constants";
import {
  getApprovalBlockReason,
  requiredDocKeysFor,
  reviewItemsOf,
} from "./paymentRequestLogic";

// Derivaciones de PRESENTACIÓN de la revisión de una solicitud de pago
// jurídica: no deciden nada, solo eligen con qué bandera del esquema homogéneo
// (REVIEW_STATUS_UI) se pinta un documento o una solicitud.
//
// Viven aquí y NO en paymentRequestLogic.js a propósito: ese archivo es espejo
// exacto del que se despliega con la Cloud Function y se mantiene sincronizado
// regla por regla. Esto es cliente puro, igual que getReviewDisplayStatus del
// flujo natural (functions.js): los estados derivados (`submitted` amarillo,
// `in_correction` rojo) no se almacenan, se calculan en lectura.

// Las fechas de la revisión viven ANIDADAS en el documento
// (documents[key].review.at, returnHistory[].at, correction.startedAt) y el
// `serialize` del backend solo convierte las de primer nivel: llegan como
// Timestamp serializado ({_seconds}) y no como ISO. Se normaliza aquí para no
// repetir el detalle en cada componente.
export const toDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  if (typeof value.toDate === "function") return value.toDate();
  const seconds = value._seconds ?? value.seconds;
  return typeof seconds === "number" ? new Date(seconds * 1000) : null;
};

export const formatReviewMoment = (value) => {
  const date = toDateValue(value);
  return date && !Number.isNaN(date.getTime())
    ? moment(date).format("YYYY-MM-DD HH:mm")
    : "sin fecha";
};

// Estado de PRESENTACIÓN de la revisión de la solicitud completa (indicador de
// la lista). Prioridad: lo terminal primero, después lo que exige trabajo de la
// empresa y por último el avance de la revisión.
export const getRequestReviewDisplayStatus = (request, catalog) => {
  if (!request) return REVIEW_STATUS.PENDING;
  if (request.status === PAYMENT_REQUEST_STATUS.APPROVED) return REVIEW_STATUS.APPROVED;
  if (request.status === PAYMENT_REQUEST_STATUS.IN_CORRECTION) return REVIEW_DISPLAY_IN_CORRECTION;
  // Un borrador no es revisable: nada se revisa antes de ser presentado.
  if (request.status !== PAYMENT_REQUEST_STATUS.SUBMITTED) return REVIEW_STATUS.PENDING;

  const items = reviewItemsOf(request, catalog);
  if (items.some((i) => i.status === REVIEW_STATUS.RETURNED)) return REVIEW_STATUS.RETURNED;
  // Sin motivo de bloqueo no queda nada por revisar: solo falta que el
  // supervisor apruebe. Se pregunta al helper para no reimplementar la regla.
  return getApprovalBlockReason({ request, catalog })
    ? REVIEW_DISPLAY_SUBMITTED
    : REVIEW_STATUS.REVIEWED;
};

// Avance de la revisión sobre los documentos EXIGIDOS a esta solicitud (los
// congelados al presentar, no el catálogo vivo). Alimenta el "n/m revisados"
// que acompaña al chip: el color dice el estado, el conteo dice cuánto falta.
export const getRequestReviewProgress = (request, catalog) => {
  const items = reviewItemsOf(request, catalog);
  const requiredKeys = requiredDocKeysFor(request, catalog);
  const required = items.filter((i) => requiredKeys.includes(i.key));
  return {
    total: required.length,
    reviewed: required.filter((i) => i.status === REVIEW_STATUS.REVIEWED).length,
    returned: items.filter((i) => i.status === REVIEW_STATUS.RETURNED).length,
  };
};
